package com.fantasytenis.shared.data.lineup.api

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.core.network.WrappedResponse
import com.fantasytenis.shared.data.lineup.dto.*
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.http.*

/**
 * Response wrapper for lineup with validation
 * Note: status defaults to 0 to handle proto3 default value omission
 */
@kotlinx.serialization.Serializable
data class LineupApiResponse(
    val status: Int = 0,
    val error: String? = null,
    val data: LineupDto? = null,
    val validation: ValidationResultDto? = null
)

/**
 * Lineup API client.
 *
 * Handles all lineup/roster-related HTTP requests.
 */
class LineupApi(
    private val httpClient: HttpClient,
    private val baseUrl: String
) {

    /**
     * Create a new lineup.
     * POST /v2/lineups
     */
    suspend fun createLineup(request: CreateLineupRequest): ApiResponse<LineupWithValidationDto> {
        return try {
            val response = httpClient.post("${baseUrl}lineups") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val apiResponse = response.body<LineupApiResponse>()
                if (apiResponse.data != null) {
                    ApiResponse.Success(
                        LineupWithValidationDto(
                            data = apiResponse.data,
                            validation = apiResponse.validation
                        )
                    )
                } else {
                    ApiResponse.Error(apiResponse.error ?: "Failed to create lineup", response.status.value)
                }
            } else {
                // Error response - handle gracefully even if response format is malformed
                val errorMessage = try {
                    val apiResponse = response.body<LineupApiResponse>()
                    apiResponse.error ?: "Failed to create lineup"
                } catch (e: Exception) {
                    // If parsing fails, return a generic error with status code
                    "Failed to create lineup (${response.status.value})"
                }
                ApiResponse.Error(errorMessage, response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Update a lineup.
     * PUT /v2/lineups/:id
     */
    suspend fun updateLineup(lineupId: String, request: UpdateLineupRequest): ApiResponse<LineupWithValidationDto> {
        return try {
            val response = httpClient.put("${baseUrl}lineups/$lineupId") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val apiResponse = response.body<LineupApiResponse>()
                if (apiResponse.data != null) {
                    ApiResponse.Success(
                        LineupWithValidationDto(
                            data = apiResponse.data,
                            validation = apiResponse.validation
                        )
                    )
                } else {
                    ApiResponse.Error(apiResponse.error ?: "Failed to update lineup", response.status.value)
                }
            } else {
                // Error response - handle gracefully even if response format is malformed
                val errorMessage = try {
                    val apiResponse = response.body<LineupApiResponse>()
                    apiResponse.error ?: "Failed to update lineup"
                } catch (e: Exception) {
                    // If parsing fails, return a generic error with status code
                    "Failed to update lineup (${response.status.value})"
                }
                ApiResponse.Error(errorMessage, response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get a lineup by ID.
     * GET /v2/lineups/:id
     */
    suspend fun getLineup(lineupId: String): ApiResponse<LineupDto> {
        return try {
            val response = httpClient.get("${baseUrl}lineups/$lineupId")

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<LineupDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Lineup not found", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<LineupDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get lineup", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Get all lineups for current user.
     * GET /v2/lineups
     */
    suspend fun getUserLineups(contestId: String? = null): ApiResponse<List<LineupDto>> {
        return try {
            val url = if (contestId != null) {
                "${baseUrl}lineups?contestId=$contestId"
            } else {
                "${baseUrl}lineups"
            }

            val response = httpClient.get(url)

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<List<LineupDto>>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Failed to get lineups", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<List<LineupDto>>>()
                ApiResponse.Error(wrapped.error?.message ?: "Failed to get lineups", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Validate a lineup (without saving).
     * POST /v2/lineups/validate
     */
    suspend fun validateLineup(request: ValidateLineupRequest): ApiResponse<ValidationResultDto> {
        return try {
            val response = httpClient.post("${baseUrl}lineups/validate") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<ValidationResultDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Validation failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<ValidationResultDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Validation failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Check if a player can be added to a slot.
     * POST /v2/lineups/:id/can-add
     */
    suspend fun canAddPlayer(lineupId: String, request: CanAddPlayerRequest): ApiResponse<CanAddPlayerResponseData> {
        return try {
            val response = httpClient.post("${baseUrl}lineups/$lineupId/can-add") {
                contentType(ContentType.Application.Json)
                setBody(request)
            }

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<CanAddPlayerResponseData>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Check failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<CanAddPlayerResponseData>>()
                ApiResponse.Error(wrapped.error?.message ?: "Check failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }

    /**
     * Lock a lineup.
     * POST /v2/lineups/:id/lock
     */
    suspend fun lockLineup(lineupId: String): ApiResponse<LineupDto> {
        return try {
            val response = httpClient.post("${baseUrl}lineups/$lineupId/lock")

            if (response.status.isSuccess()) {
                val wrapped = response.body<WrappedResponse<LineupDto>>()
                if (wrapped.data != null) {
                    ApiResponse.Success(wrapped.data)
                } else {
                    ApiResponse.Error(wrapped.error?.message ?: "Lock failed", response.status.value)
                }
            } else {
                val wrapped = response.body<WrappedResponse<LineupDto>>()
                ApiResponse.Error(wrapped.error?.message ?: "Lock failed", response.status.value)
            }
        } catch (e: Exception) {
            ApiResponse.Error(e.message ?: "Network error", 0)
        }
    }
}
